import dayjs from "dayjs";
import Image from "next/image";
import { useTranslation } from "next-i18next";

import type { WorkingArea } from "@utils";
import { workingAreasToLabelDict } from "@utils/dictionaries";

import {
  ArticleCardArea,
  ArticleCardAuthor,
  ArticleCardContainer,
  ArticleCardDate,
  ArticleCardDescription,
  ArticleCardHeader,
  ArticleCardSize,
  ImageWrapper1,
  ImageWrapper2,
  ImageWrapper3,
} from "./styles.styled";

export interface ArticleCardData {
  area?: WorkingArea;
  image: {
    src: string;
    alt: string;
  };
  title: string;
  description: string;
  date: Date;
  timeToRead: number;
  author?: string;
  href: string;
}

interface ArticleCardProps extends ArticleCardData {
  size: ArticleCardSize;
  nextImageSizes?: string;
}

export const ArticleCard = ({
  size,
  area,
  image,
  title,
  description,
  date,
  timeToRead,
  author,
  href,
  nextImageSizes,
}: ArticleCardProps) => {
  const { t } = useTranslation();

  return (
    <ArticleCardContainer href={href} size={size}>
      <ImageWrapper1 className="wrapper-1">
        <ImageWrapper2 className="wrapper-2">
          <ImageWrapper3 className="wrapper-3">
            <Image
              src={image.src}
              alt={image.alt}
              fill
              sizes={nextImageSizes}
              className="object-cover"
            />
          </ImageWrapper3>
        </ImageWrapper2>
      </ImageWrapper1>

      <div className="px-2">
        <div className="flex flex-col gap-2">
          {area && (
            <ArticleCardArea area={area}>
              {workingAreasToLabelDict(t)[area]}
            </ArticleCardArea>
          )}

          <ArticleCardHeader className="article-card-header" size={size}>
            {title}
          </ArticleCardHeader>
        </div>

        <div className={`flex flex-col ${size === "big" ? "gap-2" : "gap-1"}`}>
          <ArticleCardDescription size={size}>
            {description}
          </ArticleCardDescription>

          <ArticleCardDate>
            {[
              dayjs(date).format("ll"),
              "\u2014",
              `${timeToRead} min ${t("common.read_time_text")}`,
            ].join(" ")}
          </ArticleCardDate>

          {author && <ArticleCardAuthor>{author}</ArticleCardAuthor>}
        </div>
      </div>
    </ArticleCardContainer>
  );
};
